import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { exportLiteTmsWhsApi, getLoadingWhsListApi } from "./api";
import { AppThunk } from "../../store";
import { getErrorMessageFromBlobResponse } from "@helpers/get-error-message";
import { TMSWhsResponseProp, RequestTmsWhsParams } from "./types";

type LiteTmsWhsType = TMSWhsResponseProp & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestTmsWhsParams> | null;
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: LiteTmsWhsType = {
  items: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: {
    loading: false,
    error: null,
  },
};

const liteTmsWhs = createSlice({
  name: "liteTmsWhs",
  initialState: initialState,
  reducers: {
    getLoadingWhsStart(state: LiteTmsWhsType) {
      state.loading = true;
      state.error = null;
    },
    getLoadingWhsSuccess(
      state: LiteTmsWhsType,
      action: PayloadAction<
        TMSWhsResponseProp & { params?: RequestTmsWhsParams }
      >,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.items = items;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getLoadingWhsFailure(state: LiteTmsWhsType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    hideWarning(state: LiteTmsWhsType) {
      state.error = null;
      state.success = null;
    },
    cleanPackage(state: LiteTmsWhsType) {
      state.items = null;
      state.total = 0;
    },
    exportStart(state: LiteTmsWhsType) {
      state.exporting.loading = true;
      state.exporting.error = null;
    },
    exportSuccess(state: LiteTmsWhsType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
    exportFailure(state: LiteTmsWhsType, action: PayloadAction<string>) {
      state.exporting.loading = false;
      state.exporting.error = action.payload;
    },
  },
});

export const {
  getLoadingWhsStart,
  getLoadingWhsSuccess,
  getLoadingWhsFailure,
  hideWarning,
  cleanPackage,
  exportStart,
  exportSuccess,
  exportFailure,
} = liteTmsWhs.actions;

export default liteTmsWhs.reducer;

export const fetchLoadingWhsList =
  (params: RequestTmsWhsParams): AppThunk =>
  async (dispatch) => {
    dispatch(getLoadingWhsStart());
    try {
      const response = await getLoadingWhsListApi(params);
      dispatch(getLoadingWhsSuccess({ ...response, params }));
    } catch (error: any) {
      dispatch(getLoadingWhsFailure(error?.response?.data?.message || "Error"));
    }
  };

export const exportLiteTmsWhs =
  (params?: Partial<RequestTmsWhsParams>): AppThunk =>
  async (dispatch) => {
    dispatch(exportStart());
    try {
      const { file, filename } = await exportLiteTmsWhsApi(params);
      const url = window.URL.createObjectURL(new Blob([file]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(exportSuccess());
    } catch (error: any) {
      const message = await getErrorMessageFromBlobResponse(error);
      dispatch(exportFailure(message));
    }
  };
